/**
 * The parts every account dialog is built from: the popup, a labelled field, the
 * failure line.
 *
 * A file of their own because three dialogs in two files use them — sign-in in
 * `signInDialog.tsx`, the password change and the consent prompt in
 * `accountUi.tsx` — and a primitive that lives beside one of its callers is one
 * the others end up copying.
 */

import type { JSX } from 'react'

import { t } from './i18n'

/** A utility popup. Dismissible unless the reader must act first. */
export function Dialog({
  title,
  onClose,
  children,
}: {
  title: string
  onClose?: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="dialog">
        <header className="dialog__bar">
          <h2>{title}</h2>
          {onClose && (
            <button type="button" className="link" onClick={onClose}>
              {t('output.close')}
            </button>
          )}
        </header>
        {children}
      </div>
    </div>
  )
}

/**
 * One labelled input. Both dialogs had the same ten lines twice over, and the duplication
 * was the kind that drifts: the autocomplete token differs per field and is the part a
 * password manager reads, so a copy that quietly lost one would break sign-in for exactly
 * the readers who use one.
 */
export function Field({
  label,
  type,
  value,
  autoComplete,
  onChange,
}: {
  label: string
  type: 'email' | 'password'
  value: string
  autoComplete: string
  onChange: (value: string) => void
}): JSX.Element {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required
      />
    </label>
  )
}

/** The failure line a dialog shows when the server refused. */
export function DialogFailure({ message }: { message: string | null }): JSX.Element | null {
  if (message === null) return null
  return (
    <p className="failure" role="alert">
      {message}
    </p>
  )
}
